import { MESApiService, getMomentTz, spParameter } from "@dexteel/mesf-core";
import type {
  StrandLoss,
  StrandLossApiRow,
} from "../../../controls/ganttAsset/types/strand-loss";

const moment = getMomentTz();

export const upsertProcess = async (
  processId: number | null,
  ladleOpen: Date | null,
  ladleClose: Date | null,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });
  parameters.push({
    name: "Start",
    value: ladleOpen === null ? null : ladleOpen.toISOString(),
  });
  parameters.push({
    name: "End",
    value: ladleClose === null ? null : ladleClose.toISOString(),
  });
  parameters.push({ name: "IsManual", value: true });

  return await apiService.callV2("[CAS].[UpsertProcess]", parameters);
};

export const deleteEAFProcess = async (processId: number) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });
  return await apiService.callV2("[EAF].[DeleteProcess]", parameters);
};

export const deleteLMFProcess = async (processId: number) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });
  return await apiService.callV2("[LMF].[DeleteProcess]", parameters);
};

export const deleteCASProcess = async (processId: number) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });
  return await apiService.callV2("[CAS].[DeleteProcess]", parameters);
};

export const unDeleteProcess = async (processId: number) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });

  return await apiService.callV2("[CAS].[UnDeleteProcess]", parameters);
};

export const unLinkProcess = async (processId: number) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });

  return await apiService.callV2("[CAS].[UnlinkProcessToHeat]", parameters);
};

export const linkProcess = async (processId: number, heatId: number) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });
  parameters.push({ name: "HeatId", value: heatId });

  return await apiService.callV2("[CAS].[LinkProcessToHeat]", parameters);
};

export const searchUnlinkedHeats = async (
  searchText: string,
  ladleOpen: Date,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];

  parameters.push({
    name: "SearchText",
    value: searchText,
  });

  parameters.push({
    name: "LadleOpen",
    value: ladleOpen,
  });
  return await apiService.callV2(`[CAS].[SearchUnlinkedHeats]`, parameters);
};

export const getGanttData = async (
  Start: Date | null,
  End: Date | null,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];

  if (!moment.isDate(Start) || isNaN(Start.getTime())) {
    Start = null;
  }
  if (!moment.isDate(End) || isNaN(End.getTime())) {
    End = null;
  }

  parameters.push({ name: "Start", value: Start ? Start.toISOString() : null });
  parameters.push({ name: "End", value: End ? End.toISOString() : null });

  const response = await apiService.callV2(
    "[RBD].[GetGanttData]",
    parameters,
    signal,
  );
  const strandLosses: StrandLoss[] = response.ok
    ? ((response.data?.tables?.[2]?.rows ?? []) as StrandLossApiRow[]).map(
        (row): StrandLoss => ({
          ...row,
          Start: moment.utc(row.Start).toDate(),
          End: moment.utc(row.End).toDate(),
        }),
      )
    : [];

  return {
    ...response,
    strandLosses,
  };
};

export const getDashboardEAFProduction = async (
  Start: Date | null,
  End: Date | null,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];

  if (!moment.isDate(Start) || isNaN(Start.getTime())) {
    Start = null;
  }
  if (!moment.isDate(End) || isNaN(End.getTime())) {
    End = null;
  }

  parameters.push({ name: "Start", value: Start ? Start.toISOString() : null });
  parameters.push({ name: "End", value: End ? End.toISOString() : null });

  return await apiService.callV2(
    "[RBD].[GetDashboardEAFProduction]",
    parameters,
    signal,
  );
};

export const getDashboardLMFProduction = async (
  Start: Date | null,
  End: Date | null,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];

  if (!moment.isDate(Start) || isNaN(Start.getTime())) {
    Start = null;
  }
  if (!moment.isDate(End) || isNaN(End.getTime())) {
    End = null;
  }

  parameters.push({ name: "Start", value: Start ? Start.toISOString() : null });
  parameters.push({ name: "End", value: End ? End.toISOString() : null });

  return await apiService.callV2(
    "[RBD].[GetDashboardLMFProduction]",
    parameters,
    signal,
  );
};

export const getDashboardCASProduction = async (
  Start: Date | null,
  End: Date | null,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];

  if (!moment.isDate(Start) || isNaN(Start.getTime())) {
    Start = null;
  }
  if (!moment.isDate(End) || isNaN(End.getTime())) {
    End = null;
  }

  parameters.push({ name: "Start", value: Start ? Start.toISOString() : null });
  parameters.push({ name: "End", value: End ? End.toISOString() : null });

  return await apiService.callV2(
    "[RBD].[GetDashboardCASProduction]",
    parameters,
    signal,
  );
};

export const upsertEAFProcessData = async (
  processId: number,
  field: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches UpsertFunction contract from CommentModal
  value: any,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });
  parameters.push({ name: field, value: value });
  parameters.push({ name: "IsManual", value: true });

  return await apiService.callV2("[EAF].[UpsertProcessData]", parameters);
};

export const upsertLMFProcessData = async (
  processId: number,
  field: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches UpsertFunction contract from CommentModal
  value: any,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });
  parameters.push({ name: field, value: value });
  parameters.push({ name: "IsManual", value: true });

  return await apiService.callV2("[LMF].[UpsertProcessData]", parameters);
};

export const upsertCASProcessData = async (
  processId: number,
  field: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches UpsertFunction contract from CommentModal
  value: any,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ProcessId", value: processId });
  parameters.push({ name: field, value: value });
  parameters.push({ name: "IsManual", value: true });

  return await apiService.callV2("[CAS].[UpsertProcessData]", parameters);
};
